import type { ReactNode } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { Connector } from "@/types";
import {
  AUSLOESER,
  REGLER,
  type Befund,
  type AbschnittKey,
  type SpecEntwurf,
  type SpecKontext,
  type SystemZeile,
  type WeisungZeile,
} from "./specDocument";

// #326 (UC-1): die sechs Abschnitts-Masken. Reine Darstellung — Laden, Speichern und die
// Entwurfs-Mechanik liegen in `ProcessSpecCard`; geurteilt wird in `specDocument`
// (`pruefeEntwurf`) und endgültig im Master.
//
// Jede Maske arbeitet auf DEMSELBEN Entwurf und schreibt über `aendern` zurück. Das ist
// Absicht: gespeichert wird die ganze Definition auf einmal, weil sechs einzelne
// Speicher-Vorgänge sechs Gelegenheiten für einen halb geschriebenen Zustand wären — und
// die Abschnitte hängen zusammen (eine Rolle, die die Eskalation nennt, gehört zu den
// Rollen; ein System, das ein Schritt nutzt, gehört in die Systeme).

export interface MaskeProps {
  entwurf: SpecEntwurf;
  aendern: (patch: Partial<SpecEntwurf>) => void;
  kontext: SpecKontext;
  gesperrt: boolean;
  befunde: Befund[];
}

/** Rahmen einer Maske: Nummer, Titel, Erklärsatz, eigene Befunde. */
export function Abschnitt({
  nummer,
  titel,
  erklaerung,
  abschnitt,
  befunde,
  nichtDarstellbar,
  children,
  testId,
}: {
  nummer: number;
  titel: string;
  erklaerung: ReactNode;
  abschnitt: AbschnittKey;
  befunde: Befund[];
  nichtDarstellbar: boolean;
  children: ReactNode;
  testId: string;
}) {
  const eigene = befunde.filter((b) => b.abschnitt === abschnitt);
  return (
    <div className="space-y-2.5 border-t border-border pt-4 first:border-t-0 first:pt-0" data-testid={testId}>
      <div>
        <div className="text-[13px] font-semibold text-text">
          {nummer}. {titel}
        </div>
        <div className="mt-0.5 text-[12px] leading-relaxed text-text3">{erklaerung}</div>
      </div>
      {nichtDarstellbar ? (
        <p className="rounded-md border border-warn-border bg-warn-bg px-3 py-2 text-[12.5px]" data-testid={`${testId}-unlesbar`}>
          Dieser Abschnitt liegt in einer Form vor, die diese Maske nicht darstellen kann. Er
          bleibt beim Speichern unverändert; ändern lässt er sich nur im Inhalt der Definition.
        </p>
      ) : (
        children
      )}
      {eigene.length > 0 && (
        <ul className="space-y-1" data-testid={`${testId}-befunde`}>
          {eigene.map((b, i) => (
            <li
              key={i}
              className={b.art === "fehler" ? "text-[12px] text-danger" : "text-[12px] text-warn"}
            >
              {b.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- 1 · Auslöser -------------------------------------------------------------------------

export function AusloeserMaske({ entwurf, aendern, gesperrt, befunde }: MaskeProps) {
  const umschalten = (kind: string, an: boolean): void => {
    const ohne = entwurf.triggerKinds.filter((k) => k !== kind);
    aendern({ triggerKinds: an ? [...ohne, kind] : ohne });
  };
  // Werte, die im Dokument stehen, aber keine der sechs Arten sind: sichtbar lassen (der
  // Master lehnt sie ab) statt sie beim Speichern still zu verlieren.
  const fremde = entwurf.triggerKinds.filter((k) => !AUSLOESER.some((a) => a.kind === k));
  return (
    <Abschnitt
      nummer={1}
      titel="Auslöser"
      erklaerung="Wodurch darf ein Vorgang dieses Prozesses starten? Die Vorlage steckt den Rahmen; die Einrichtung wählt daraus GENAU einen und richtet ihn ein."
      abschnitt="trigger"
      befunde={befunde}
      nichtDarstellbar={entwurf.nichtDarstellbar.includes("trigger")}
      testId="spec-trigger"
    >
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {AUSLOESER.map((a) => (
          <label key={a.kind} className="flex items-center gap-2 text-[12.5px]">
            <input
              type="checkbox"
              className="h-3.5 w-3.5"
              checked={entwurf.triggerKinds.includes(a.kind)}
              onChange={(e) => umschalten(a.kind, e.target.checked)}
              disabled={gesperrt}
              data-testid={`spec-trigger-${a.kind}`}
            />
            <span>{a.label}</span>
            {!a.einrichtbar && <span className="text-[11px] text-text3">(noch nicht einrichtbar)</span>}
          </label>
        ))}
      </div>
      {fremde.length > 0 && (
        <p className="text-[12px] text-warn" data-testid="spec-trigger-fremd">
          Im Dokument stehen ausserdem unbekannte Arten: {fremde.join(", ")}. Sie bleiben stehen —
          der Master lehnt die Definition damit ab.
        </p>
      )}
      <p className="text-[12px] text-text3">
        „Noch nicht einrichtbar" heisst: die Art darf hier deklariert werden, an der Einrichtung
        lässt sie sich noch nicht konfigurieren.
      </p>
    </Abschnitt>
  );
}

// --- 2 · Instanziierungs-Rechte ------------------------------------------------------------

export function InstanziierungsMaske({ entwurf, aendern, kontext, gesperrt, befunde }: MaskeProps) {
  const gewaehlt = entwurf.instantiation ?? [];
  const umschalten = (rolle: string, an: boolean): void => {
    const ohne = gewaehlt.filter((r) => r !== rolle);
    aendern({ instantiation: an ? [...ohne, rolle] : ohne });
  };
  const fremde = gewaehlt.filter((r) => !kontext.rollen.includes(r));
  return (
    <Abschnitt
      nummer={2}
      titel="Instanziierungs-Rechte"
      erklaerung="Welche Fach-Rollen dürfen aus dieser Vorlage eine eigene Einrichtung machen? Wirksam wird die Auswahl mit der AKTIVIERUNG einer Version — bis dahin gilt, was bei der letzten Aktivierung übernommen wurde."
      abschnitt="instantiation"
      befunde={befunde}
      nichtDarstellbar={entwurf.nichtDarstellbar.includes("instantiation")}
      testId="spec-instantiation"
    >
      {kontext.rollen.length === 0 ? (
        <p className="text-[12.5px] text-text3" data-testid="spec-instantiation-leer">
          Dieser Prozess deklariert noch keine Fach-Rollen. Sie stehen im Abschnitt „rollen" der
          Definition und lassen sich nur dort ergänzen.
        </p>
      ) : (
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {kontext.rollen.map((r) => (
            <label key={r} className="flex items-center gap-2 text-[12.5px]">
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={gewaehlt.includes(r)}
                onChange={(e) => umschalten(r, e.target.checked)}
                disabled={gesperrt}
                data-testid={`spec-instantiation-${r}`}
              />
              <span>{r}</span>
            </label>
          ))}
        </div>
      )}
      {fremde.length > 0 && (
        <div className="space-y-1" data-testid="spec-instantiation-fremd">
          {fremde.map((r) => (
            <label key={r} className="flex items-center gap-2 text-[12.5px]">
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked
                onChange={() => umschalten(r, false)}
                disabled={gesperrt}
              />
              <span>{r}</span>
              <span className="text-[11px] text-danger">nicht deklariert</span>
            </label>
          ))}
        </div>
      )}
      <p className="text-[12px] text-text3">
        Ohne Auswahl entscheidet allein die Zuweisung an Personen (Abschnitt
        „Instanziierungs-Berechtigungen") plus die verantwortliche Person und Admins.
      </p>
    </Abschnitt>
  );
}

// --- 3 · Systeme ---------------------------------------------------------------------------

export function SystemeMaske({
  entwurf,
  aendern,
  gesperrt,
  befunde,
  connectors,
  connectorsFehler,
}: MaskeProps & { connectors: Connector[] | null; connectorsFehler: boolean }) {
  const setzen = (schluessel: string, patch: Partial<SystemZeile>): void => {
    aendern({
      systeme: entwurf.systeme.map((s) => (s.schluessel === schluessel ? { ...s, ...patch } : s)),
    });
  };
  const entfernen = (schluessel: string): void => {
    aendern({ systeme: entwurf.systeme.filter((s) => s.schluessel !== schluessel) });
  };
  const hinzufuegen = (): void => {
    const seq = entwurf.seq + 1;
    aendern({
      seq,
      systeme: [...entwurf.systeme, { schluessel: `z${seq}`, roh: undefined, connectorId: "", access: "read", zweck: "" }],
    });
  };
  return (
    <Abschnitt
      nummer={3}
      titel="Systeme"
      erklaerung="Mit welchen Zielsystemen arbeitet dieser Prozess, und in welchem Umfang? Das ist die Governance-Ebene: hier steht, WAS erlaubt ist — welcher Schritt es tatsächlich benutzt, steht am Schritt."
      abschnitt="systems"
      befunde={befunde}
      nichtDarstellbar={entwurf.nichtDarstellbar.includes("systems")}
      testId="spec-systems"
    >
      {connectorsFehler && (
        <p className="text-[12px] text-warn" data-testid="spec-systems-katalog-fehler">
          Der Connector-Katalog ist gerade nicht abrufbar — bestehende Einträge bleiben
          unverändert, neue lassen sich nicht auswählen.
        </p>
      )}
      {entwurf.systeme.length === 0 ? (
        <p className="text-[12.5px] text-text3">Noch kein System deklariert.</p>
      ) : (
        <div className="space-y-2">
          {entwurf.systeme.map((s) => {
            // ⚠ Review M2: „auflösbar" darf NUR aus einer wirklich gelesenen Liste kommen.
            // Vorher galt ein nicht abrufbarer Katalog als „alles bekannt" — dann fiel die
            // Ersatz-Option weg, das Auswahlfeld stand auf „— Zielsystem wählen —", und der
            // naheliegende reparierende Klick hätte eine gültige Deklaration wirklich geleert.
            // Genau dieser Fall ist der REGELFALL für alle ohne Katalog-Leserecht.
            const aufloesbar = connectors !== null && connectors.some((c) => c.id === s.connectorId);
            return (
              <div key={s.schluessel} className="flex flex-wrap items-center gap-2" data-testid="spec-system-zeile">
                <select
                  className="h-9 w-64 rounded-md border border-border bg-surface px-2 text-[13px]"
                  value={s.connectorId}
                  onChange={(e) => setzen(s.schluessel, { connectorId: e.target.value })}
                  disabled={gesperrt}
                  data-testid="spec-system-connector"
                >
                  <option value="">— Zielsystem wählen —</option>
                  {/* Ein Eintrag, den das Auswahlfeld nicht auflösen kann, bleibt wählbar —
                      sonst fiele es auf „leer" zurück und löschte beim Speichern still eine
                      bestehende Deklaration. Die BESCHRIFTUNG sagt, woran es liegt: fehlt der
                      Katalog, ist über den Eintrag nichts bekannt (blosse Kennung); ist er da,
                      steht der Eintrag wirklich nicht drin. */}
                  {!aufloesbar && s.connectorId !== "" && (
                    <option value={s.connectorId}>
                      {connectors === null ? s.connectorId : `${s.connectorId} (nicht im Katalog)`}
                    </option>
                  )}
                  {(connectors ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <select
                  className="h-9 w-40 rounded-md border border-border bg-surface px-2 text-[13px]"
                  value={s.access}
                  onChange={(e) => setzen(s.schluessel, { access: e.target.value })}
                  disabled={gesperrt}
                  data-testid="spec-system-access"
                >
                  <option value="read">nur lesen</option>
                  <option value="write">auch schreiben</option>
                </select>
                <Input
                  className="w-72"
                  value={s.zweck}
                  onChange={(e) => setzen(s.schluessel, { zweck: e.target.value })}
                  placeholder="Wozu? (optional)"
                  disabled={gesperrt}
                  data-testid="spec-system-zweck"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => entfernen(s.schluessel)}
                  disabled={gesperrt}
                  data-testid="spec-system-entfernen"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <Button size="sm" variant="outline" onClick={hinzufuegen} disabled={gesperrt} data-testid="spec-system-hinzufuegen">
        <Plus className="h-4 w-4" />
        System hinzufügen
      </Button>
      <p className="text-[12px] text-text3">
        Ein schreibendes System wirkt nur in den Schritten, die es ausdrücklich aufführen — ein
        lesendes steht jedem Agent-Schritt offen.
      </p>
    </Abschnitt>
  );
}

// --- 4 · Start-Eingaben --------------------------------------------------------------------

type EingabeStand = "aus" | "pflicht" | "optional";

export function EingabenMaske({ entwurf, aendern, kontext, gesperrt, befunde }: MaskeProps) {
  const stand = (key: string): EingabeStand =>
    entwurf.eingabenPflicht.includes(key) ? "pflicht" : entwurf.eingabenOptional.includes(key) ? "optional" : "aus";

  const setzen = (key: string, neu: EingabeStand): void => {
    const pflicht = entwurf.eingabenPflicht.filter((k) => k !== key);
    const optional = entwurf.eingabenOptional.filter((k) => k !== key);
    if (neu === "pflicht") pflicht.push(key);
    if (neu === "optional") optional.push(key);
    aendern({ eingabenPflicht: pflicht, eingabenOptional: optional, eingabenVorhanden: true });
  };

  const bekannt = new Set(kontext.casefields.map((f) => f.key));
  const fremde = [...entwurf.eingabenPflicht, ...entwurf.eingabenOptional].filter((k) => !bekannt.has(k));

  return (
    <Abschnitt
      nummer={4}
      titel="Start-Eingaben"
      erklaerung="Welche Vorgangsfelder muss (oder darf) der Auslöser beim Start mitliefern? Auswählbar ist nur, was der Prozess als Vorgangsfeld kennt; ein Feld ist entweder Pflicht oder optional, nie beides."
      abschnitt="input"
      befunde={befunde}
      nichtDarstellbar={entwurf.nichtDarstellbar.includes("input")}
      testId="spec-input"
    >
      {kontext.casefields.length === 0 ? (
        <p className="text-[12.5px] text-text3" data-testid="spec-input-leer">
          Dieser Prozess kennt noch keine Vorgangsfelder. Sie stehen im Abschnitt „casefields" der
          Definition und lassen sich nur dort ergänzen.
        </p>
      ) : (
        <div className="space-y-2">
          {kontext.casefields.map((f) => (
            <div key={f.key} className="flex flex-wrap items-center gap-3" data-testid="spec-input-zeile">
              <span className="w-64 text-[12.5px]">
                {f.label || f.key} <span className="font-mono text-[11.5px] text-text3">{f.key}</span>
              </span>
              <select
                className="h-9 w-48 rounded-md border border-border bg-surface px-2 text-[13px]"
                value={stand(f.key)}
                onChange={(e) => setzen(f.key, e.target.value as EingabeStand)}
                disabled={gesperrt}
                data-testid={`spec-input-${f.key}`}
              >
                <option value="aus">nicht beim Start</option>
                <option value="pflicht">Pflicht</option>
                <option value="optional">optional</option>
              </select>
            </div>
          ))}
        </div>
      )}
      {fremde.length > 0 && (
        <div className="space-y-1" data-testid="spec-input-fremd">
          {fremde.map((k) => (
            <div key={k} className="flex items-center gap-3 text-[12.5px]">
              <span className="w-64 font-mono text-[11.5px]">{k}</span>
              <span className="text-danger">kein Vorgangsfeld</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  aendern({
                    eingabenPflicht: entwurf.eingabenPflicht.filter((x) => x !== k),
                    eingabenOptional: entwurf.eingabenOptional.filter((x) => x !== k),
                  })
                }
                disabled={gesperrt}
                data-testid="spec-input-fremd-entfernen"
              >
                entfernen
              </Button>
            </div>
          ))}
        </div>
      )}
    </Abschnitt>
  );
}

// --- 5 · Weisungen -------------------------------------------------------------------------

export function WeisungenMaske({ entwurf, aendern, gesperrt, befunde }: MaskeProps) {
  const setzen = (schluessel: string, patch: Partial<WeisungZeile>): void => {
    aendern({
      weisungen: entwurf.weisungen.map((w) => (w.schluessel === schluessel ? { ...w, ...patch } : w)),
    });
  };
  const entfernen = (schluessel: string): void => {
    aendern({ weisungen: entwurf.weisungen.filter((w) => w.schluessel !== schluessel) });
  };
  const hinzufuegen = (): void => {
    const seq = entwurf.seq + 1;
    aendern({
      seq,
      weisungen: [
        ...entwurf.weisungen,
        { schluessel: `z${seq}`, roh: undefined, id: "", autoritaet: "verbindlich", text: "", quelle: "" },
      ],
    });
  };
  return (
    <Abschnitt
      nummer={5}
      titel="Weisungen"
      erklaerung="Welche fachlichen Vorgaben gelten in diesem Prozess? Ein Schritt kann sich auf eine Weisung berufen. In v1 sind sie deklarativ: sie stehen in der Definition und gehen in die Anweisung des Agenten ein — erzwungen werden sie nicht."
      abschnitt="weisungen"
      befunde={befunde}
      nichtDarstellbar={entwurf.nichtDarstellbar.includes("weisungen")}
      testId="spec-weisungen"
    >
      {entwurf.weisungen.length === 0 ? (
        <p className="text-[12.5px] text-text3">Noch keine Weisung hinterlegt.</p>
      ) : (
        <div className="space-y-3">
          {entwurf.weisungen.map((w) => (
            <div key={w.schluessel} className="space-y-2 rounded-md border border-border bg-surface2 p-3" data-testid="spec-weisung-zeile">
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  className="w-40"
                  value={w.id}
                  onChange={(e) => setzen(w.schluessel, { id: e.target.value })}
                  placeholder="Kennung (z.B. w1)"
                  disabled={gesperrt}
                  data-testid="spec-weisung-id"
                />
                <select
                  className="h-9 w-44 rounded-md border border-border bg-surface px-2 text-[13px]"
                  value={w.autoritaet}
                  onChange={(e) => setzen(w.schluessel, { autoritaet: e.target.value })}
                  disabled={gesperrt}
                  data-testid="spec-weisung-autoritaet"
                >
                  <option value="verbindlich">verbindlich</option>
                  <option value="orientierend">orientierend</option>
                </select>
                <Input
                  className="w-64"
                  value={w.quelle}
                  onChange={(e) => setzen(w.schluessel, { quelle: e.target.value })}
                  placeholder="Quelle (optional)"
                  disabled={gesperrt}
                  data-testid="spec-weisung-quelle"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => entfernen(w.schluessel)}
                  disabled={gesperrt}
                  data-testid="spec-weisung-entfernen"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <Textarea
                rows={2}
                value={w.text}
                onChange={(e) => setzen(w.schluessel, { text: e.target.value })}
                placeholder="Was gilt? (z.B. Keine Buchung ohne Leistungsnachweis.)"
                disabled={gesperrt}
                data-testid="spec-weisung-text"
              />
            </div>
          ))}
        </div>
      )}
      <Button size="sm" variant="outline" onClick={hinzufuegen} disabled={gesperrt} data-testid="spec-weisung-hinzufuegen">
        <Plus className="h-4 w-4" />
        Weisung hinzufügen
      </Button>
    </Abschnitt>
  );
}

// --- 6 · Regler ----------------------------------------------------------------------------

export function ReglerMaske({ entwurf, aendern, kontext, gesperrt, befunde }: MaskeProps) {
  const setzen = (key: string, patch: Partial<SpecEntwurf["regler"][string]>): void => {
    const alt = entwurf.regler[key];
    if (!alt) return;
    aendern({ regler: { ...entwurf.regler, [key]: { ...alt, ...patch } } });
  };
  return (
    <Abschnitt
      nummer={6}
      titel="Regler"
      erklaerung="Die Stellschrauben des Prozesses. Je Regler entscheidet die Vorlage zusätzlich, ob eine Einrichtung ihn ändern darf — was hier nicht freigegeben ist, gilt dort unverrückbar."
      abschnitt="settings"
      befunde={befunde}
      nichtDarstellbar={entwurf.nichtDarstellbar.includes("settings")}
      testId="spec-settings"
    >
      <div className="space-y-3">
        {REGLER.map((r) => {
          const zeile = entwurf.regler[r.key];
          if (!zeile) return null;
          return (
            <div key={r.key} className="space-y-1.5" data-testid={`spec-regler-${r.key}`}>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex w-64 items-center gap-2 text-[12.5px]">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5"
                    checked={zeile.gesetzt}
                    onChange={(e) => setzen(r.key, { gesetzt: e.target.checked })}
                    /* ⚠ Ein Eintrag ohne Objekt-Form ist VOLLSTÄNDIG gesperrt (Review H1):
                       diese Maske kann ihn weder anzeigen noch patchen, ohne seinen Wert zu
                       zerstören — auch das Ausschalten wäre eine Änderung an etwas, das man
                       gerade nicht sieht. */
                    disabled={gesperrt || zeile.formFremd}
                    data-testid={`spec-regler-${r.key}-gesetzt`}
                  />
                  <span className={zeile.gesetzt ? "font-medium text-text" : "text-text3"}>{r.label}</span>
                </label>
                {zeile.formFremd ? (
                  <span className="text-[12px] text-warn" data-testid={`spec-regler-${r.key}-formfremd`}>
                    Dieser Regler steht nicht in der erwarteten Form (erwartet wird ein Eintrag mit
                    Wert und Freigabe). Er bleibt unverändert und ist nur im Inhalt der Definition
                    änderbar.
                  </span>
                ) : zeile.fremdTyp ? (
                  <span className="text-[12px] text-warn" data-testid={`spec-regler-${r.key}-fremdtyp`}>
                    Der hinterlegte Wert hat eine andere Form als dieses Feld — er bleibt
                    unverändert und ist nur im Inhalt der Definition änderbar.
                  </span>
                ) : r.art === "rolle" ? (
                  <select
                    className="h-9 w-56 rounded-md border border-border bg-surface px-2 text-[13px]"
                    value={zeile.wert}
                    onChange={(e) => setzen(r.key, { wert: e.target.value })}
                    disabled={gesperrt || !zeile.gesetzt}
                    data-testid={`spec-regler-${r.key}-wert`}
                  >
                    <option value="">— Rolle wählen —</option>
                    {kontext.rollen.map((rolle) => (
                      <option key={rolle} value={rolle}>
                        {rolle}
                      </option>
                    ))}
                    {zeile.wert !== "" && !kontext.rollen.includes(zeile.wert) && (
                      <option value={zeile.wert}>{zeile.wert} (nicht deklariert)</option>
                    )}
                  </select>
                ) : (
                  <Input
                    className="w-56"
                    value={zeile.wert}
                    onChange={(e) => setzen(r.key, { wert: e.target.value })}
                    placeholder={r.platzhalter}
                    disabled={gesperrt || !zeile.gesetzt}
                    data-testid={`spec-regler-${r.key}-wert`}
                  />
                )}
                <label className="flex items-center gap-2 text-[12.5px]">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5"
                    checked={zeile.overridable}
                    onChange={(e) => setzen(r.key, { overridable: e.target.checked })}
                    disabled={gesperrt || !zeile.gesetzt || zeile.formFremd}
                    data-testid={`spec-regler-${r.key}-overridable`}
                  />
                  <span className={zeile.gesetzt ? "" : "text-text3"}>Einrichtung darf ändern</span>
                </label>
              </div>
              <p className="ml-6 text-[11.5px] leading-relaxed text-text3">{r.info}</p>
            </div>
          );
        })}
      </div>
      {/* Review L5: unbekannte Schlüssel im Abschnitt benennen (Muster: fremde Auslöser). */}
      {entwurf.fremdeRegler.length > 0 && (
        <p className="text-[12px] text-warn" data-testid="spec-settings-fremd">
          Im Abschnitt stehen ausserdem unbekannte Regler: {entwurf.fremdeRegler.join(", ")}. Sie
          bleiben stehen — der Master lehnt die Definition damit ab.
        </p>
      )}
    </Abschnitt>
  );
}
